'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useWizardStore } from '@/stores/wizardStore';

// This is the part of the progress bar that is left to be filled after success step
const SUCCESS_BAR_WIDTH = 15;

export default function StepperProgressBar() {
  const steps = useWizardStore((s) => s.steps);
  const currentStep = useWizardStore((s) => s.currentStep);
  const stepsGap = useWizardStore((s) => s.stepsGap);
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false);
  const [barWidth, setBarWidth] = useState(0);
  const [isReady, setIsReady] = useState(false);

  // Calculate total width of all steps + gaps
  const totalStepsWidth = useMemo(() => {
    return steps.reduce((acc, step, index) => {
      const stepWidth = step.width || 0;
      const gapWidth = index < steps.length - 1 ? stepsGap || 46 : 0;
      return acc + stepWidth + gapWidth;
    }, 0);
  }, [steps, stepsGap]);

  // Calculate completed width
  const completedWidth = useMemo(() => {
    return steps.slice(0, currentStep + 1).reduce((acc, step, index) => {
      const stepWidth = step.width || 0;
      const gapWidth = index < currentStep ? stepsGap || 46 : 0;
      return acc + stepWidth + gapWidth;
    }, 0);
  }, [steps, currentStep, stepsGap]);

  const calculateWidth = useCallback(() => {
    const isSmall = window.innerWidth < 536;
    setIsVerySmallScreen(isSmall);

    // Calculate dynamic bar width
    let newBarWidth;

    if (isSmall) {
      newBarWidth = window.innerWidth; // Use full width on small screens
    } else {
      newBarWidth = Math.max(totalStepsWidth, 448);
    }

    setBarWidth(newBarWidth);
    setIsReady(true);
  }, [totalStepsWidth]);

  const debouncedCalculateWidth = useDebouncedCallback(calculateWidth, 300);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateWidth();
    }, 0);

    window.addEventListener('resize', debouncedCalculateWidth);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', debouncedCalculateWidth);
    };
  }, [calculateWidth, debouncedCalculateWidth]);

  if (!steps || steps.length === 0 || !isReady) return null;

  const offset = Math.max((barWidth - totalStepsWidth) / 2, 0);

  // Always use offset + completedWidth for the fill width
  let fillWidthPx = offset + completedWidth;

  // On the last step, fill to the end minus SUCCESS_BAR_WIDTH
  if (currentStep === steps.length - 1) {
    fillWidthPx = barWidth - SUCCESS_BAR_WIDTH;
  }
  // On the success/confirmation step (if you have one), fill completely
  if (currentStep === steps.length) {
    fillWidthPx = barWidth;
  }

  return (
    <div
      className="relative w-full"
      style={{
        maxWidth: barWidth,
        minWidth: isVerySmallScreen ? undefined : '448px',
      }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round((fillWidthPx / barWidth) * 100)}
      aria-label="Step progress"
    >
      {/* Background bar */}
      <div className="bg-grey-400 dark:bg-grey-600 absolute top-[-5px] h-[10px] w-full rounded" />

      {/* Active progress bar */}
      <div
        className="bg-secondary absolute h-[10px] rounded transition-all"
        style={{
          left: 0,
          width: `${fillWidthPx}px`,
          maxWidth: '100%',
          transform: 'translateY(-50%)',
        }}
      />
    </div>
  );
}
